/**
 * HR Employee Flags API client.
 *
 * Only callable by HR / Director / ExecDirector — the API enforces this.
 * Flags are observational (no priority-score effect).
 */

import { apiRequest } from './client'

export type FlagSeverity = 'low' | 'medium' | 'high'
export type FlagSource = 'ai_pattern' | 'ai_reason_judge' | 'hr_manual'

export interface EmployeeFlag {
  id: string
  employeeId: string
  flagCode: string
  severity: FlagSeverity
  source: FlagSource
  sourceRef: string | null
  reason: string
  /** Structured tags applied to the flag, e.g. ['perf_review']. */
  tags: string[]
  createdAt: string
  resolvedAt: string | null
  resolvedByEmployeeId: string | null
  resolutionNote: string | null
}

export interface CreateFlagInput {
  flagCode: string
  severity: FlagSeverity
  reason: string
}

export interface ResolveFlagInput {
  resolutionNote: string
}

export const VALID_FLAG_CODES = [
  'pattern_frequent_sick_leave',
  'pattern_poor_planning_emergency',
  'pattern_emergency_approaching_limit',
  'pattern_unpaid_leave_taken',
  'pattern_frequent_half_days',
  'pattern_long_weekend_pattern',
  'pattern_no_leave_in_window',
  'pattern_high_early_utilisation',
  'reason_quality_low',
  'reason_doc_mismatch',
  'hr_custom',
] as const

export type FlagCode = typeof VALID_FLAG_CODES[number]

export const FLAG_CODE_LABELS: Record<FlagCode, string> = {
  pattern_frequent_sick_leave: 'Frequent Sick Leave',
  pattern_poor_planning_emergency: 'Poor Planning (Emergency)',
  pattern_emergency_approaching_limit: 'Emergency Approaching Limit',
  pattern_unpaid_leave_taken: 'Unpaid Leave Taken',
  pattern_frequent_half_days: 'Frequent Half-Days',
  pattern_long_weekend_pattern: 'Long-Weekend Pattern',
  pattern_no_leave_in_window: 'No Leave in Window',
  pattern_high_early_utilisation: 'High Early Utilisation',
  reason_quality_low: 'Low Reason Quality',
  reason_doc_mismatch: 'Document Mismatch',
  hr_custom: 'HR Custom',
}

export const FLAG_CODE_DEFAULT_SEVERITY: Record<FlagCode, FlagSeverity> = {
  pattern_frequent_sick_leave: 'high',
  pattern_poor_planning_emergency: 'medium',
  pattern_emergency_approaching_limit: 'high',
  pattern_unpaid_leave_taken: 'medium',
  pattern_frequent_half_days: 'low',
  pattern_long_weekend_pattern: 'medium',
  pattern_no_leave_in_window: 'medium',
  pattern_high_early_utilisation: 'medium',
  reason_quality_low: 'medium',
  reason_doc_mismatch: 'high',
  hr_custom: 'medium',
}

export function humaniseFlagCode(code: string): string {
  return FLAG_CODE_LABELS[code as FlagCode] ?? code
}

export function listEmployeeFlags(
  employeeId: string,
  includeResolved = false,
): Promise<EmployeeFlag[]> {
  const qs = includeResolved ? '?include_resolved=true' : ''
  return apiRequest<EmployeeFlag[]>(
    `/api/v1/hr/employees/${encodeURIComponent(employeeId)}/flags${qs}`,
  )
}

export function createEmployeeFlag(
  employeeId: string,
  input: CreateFlagInput,
): Promise<EmployeeFlag> {
  return apiRequest<EmployeeFlag>(
    `/api/v1/hr/employees/${encodeURIComponent(employeeId)}/flags`,
    { method: 'POST', body: input },
  )
}

export function resolveEmployeeFlag(
  employeeId: string,
  flagId: string,
  input: ResolveFlagInput,
): Promise<EmployeeFlag> {
  return apiRequest<EmployeeFlag>(
    `/api/v1/hr/employees/${encodeURIComponent(employeeId)}/flags/${encodeURIComponent(flagId)}/resolve`,
    { method: 'PATCH', body: input },
  )
}
